import React, { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { format } from 'date-fns';
import Button from 'components/Button';
import Message from 'components/Message';
import { useEffectDidUpdate } from 'helpers/react-hooks';
import './DatePicker.scss';

const messages = defineMessages({
  done: {
    id: 'date-picker.done',
    defaultMessage: 'Done',
  },
});

const DatePicker = ({
  className,
  components,
  dateFnsLocale,
  defaultValue,
  isDesktop,
  isValidDate = null,
  modifiers,
  modifiersClassNames,
  month,
  onChange,
  onMonthChange,
  pickerFooter,
}) => {
  const [selectedDate, setSelectedDate] = useState(defaultValue);
  const handleOnDayClick = (day, { disabled }) => {
    let validDate = !disabled;
    if (isValidDate) {
      validDate = isValidDate(day);
    }

    if (validDate) {
      return setSelectedDate(day);
    }

    return setSelectedDate(defaultValue);
  };

  useEffectDidUpdate(() => {
    if (isDesktop) {
      onChange(selectedDate);
    }
  }, [selectedDate]);

  return (
    <div className={classNames('date-picker', className)}>
      <DayPicker
        components={components}
        data-cypress="react-day-picker"
        defaultMonth={selectedDate}
        enableOutsideDaysClick
        formatters={{
          formatWeekdayName: weekday => format(weekday, 'iiiii', { locale: dateFnsLocale }),
        }}
        locale={dateFnsLocale}
        mode="single"
        modifiers={modifiers}
        modifiersClassNames={modifiersClassNames}
        month={month}
        onDayClick={handleOnDayClick}
        onMonthChange={onMonthChange}
        selected={selectedDate}
        showOutsideDays
      />
      {pickerFooter && <div className="date-picker__extra">{pickerFooter}</div>}
      {!isDesktop && (
        <div className="date-picker__footer">
          <Message
            message={messages.done}
            as={Button}
            className="date-picker__done-button"
            onClick={() => onChange(selectedDate)}
          />
        </div>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  className: PropTypes.string,
  components: PropTypes.objectOf(PropTypes.elementType),
  dateFnsLocale: PropTypes.shape({}),
  defaultValue: PropTypes.instanceOf(Date),
  isDesktop: PropTypes.bool,
  isValidDate: PropTypes.func,
  modifiers: PropTypes.shape({
    disabled: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ after: PropTypes.instanceOf(Date) })]),
    today: PropTypes.instanceOf(Date),
  }),
  modifiersClassNames: PropTypes.objectOf(PropTypes.string),
  month: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  onMonthChange: PropTypes.func,
  pickerFooter: PropTypes.node,
};

export default DatePicker;
